"use client";

import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ProfileLink } from "@/components/profile/profile-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useProfileBuddies } from "@/hooks/use-buddies";

interface BuddiesDrawerProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BuddiesDrawer({
  userId,
  open,
  onOpenChange,
}: BuddiesDrawerProps) {
  const [query, setQuery] = useState("");

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useProfileBuddies(userId, {
    enabled: open,
    query: query,
  });

  const buddies = data?.pages.flatMap((page) => page.buddies) ?? [];

  // Infinite scroll. The list scrolls inside a fixed-height box, so the
  // observer is rooted on that box: against the viewport the sentinel would
  // count as visible on open and every page would load at once. Re-observing
  // after each page fires again while the sentinel is still in reach, so a
  // short page keeps filling until it can scroll.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!scroller || !sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
      },
      { root: scroller, rootMargin: "0px 0px 320px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scroller, sentinel, hasNextPage, isFetchingNextPage, fetchNextPage]);
  const mutualCount = data?.pages[0]?.mutualCount ?? 0;

  useEffect(() => {
    if (!error) return;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Failed to load buddies";
    toast.error(message);
  }, [error]);

  const initials = (value: string) => {
    const cleaned = value.trim();
    if (!cleaned) return "";
    const parts = cleaned.split(" ");
    if (parts.length === 1) {
      return parts[0]?.slice(0, 2).toUpperCase() ?? "";
    }
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="z-200 mx-auto w-full max-w-lg rounded-t-[28px] border-border bg-background">
        <DrawerHeader className="pb-0 text-center">
          <DrawerTitle className="text-base font-semibold text-text-primary">
            Buddies
          </DrawerTitle>
        </DrawerHeader>

        <div className="space-y-2 px-4 py-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-text-secondary" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search buddies"
              className="pl-9"
            />
          </div>
          {query.trim() === "" && mutualCount > 0 ? (
            <p className="px-1 text-xs text-text-secondary">
              {mutualCount} mutual{mutualCount === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>

        <Separator className="bg-border" />

        <div
          ref={setScroller}
          className="no-scrollbar flex h-[60vh] flex-col gap-2 overflow-y-auto px-4 pt-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
        >
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center text-text-secondary">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : buddies.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-text-secondary">
              <p className="text-sm">
                {query === ""
                  ? "No buddies found."
                  : "No matches for your search."}
              </p>
            </div>
          ) : (
            <>
              {buddies.map((buddy) => (
                <ProfileLink
                  key={buddy.id}
                  handle={buddy.username}
                  fallbackAs="div"
                  className="flex items-center justify-between gap-3 rounded-xl p-2 transition-colors hover:bg-surface-alt/50 active:bg-surface-alt"
                  onClick={() => onOpenChange(false)}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="h-10 w-10 border border-border">
                      <AvatarImage src={buddy.image ?? undefined} />
                      <AvatarFallback>{initials(buddy.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold text-text-primary">
                        {buddy.name}
                      </span>
                      {buddy.username && (
                        <span className="truncate text-xs text-text-secondary">
                          @{buddy.username}
                        </span>
                      )}
                    </div>
                  </div>
                  {buddy.isMutual ? (
                    <Badge
                      variant="secondary"
                      className="shrink-0 bg-brand/10 text-[10px] text-brand"
                    >
                      Mutual
                    </Badge>
                  ) : null}
                </ProfileLink>
              ))}

              {hasNextPage && (
                <div
                  ref={setSentinel}
                  className="flex h-10 shrink-0 items-center justify-center text-text-secondary"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
